/**
 * FaxOfficesModal — the recruiter supplies each office's fax number (and may
 * add/adjust the location) for a SIGNED records release, then sends (Phase E.2).
 * The patient named the office(s) but usually didn't have the fax; the fax +
 * location print on the cover page. POST /documents/:signed_document_id/fax
 * persists them and queues one fax per office that isn't already delivered/in
 * flight. Reopen to add faxes for more offices or retry a failed one.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { ProviderOffice, RecordsRequest } from '../../types';

interface Props {
  request: RecordsRequest;
  onClose: () => void;
  /** Called only when at least one fax was actually queued, with the count. */
  onSent: (queuedCount: number) => void;
}

interface Row { name: string; city_state: string; fax: string; }

const faxValid = (fax: string) => {
  const d = (fax || '').replace(/\D/g, '');
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
};
const last10 = (s: string | null) => (s || '').replace(/\D/g, '').slice(-10);
const fmtFax = (fax: string | null) => {
  const d = (fax || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : fax || '';
};

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const lblCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400';

export default function FaxOfficesModal({ request, onClose, onSent }: Props) {
  const [offices, setOffices] = useState<Row[]>(
    (request.offices || []).map((o) => ({ name: o.name, city_state: o.city_state || '', fax: o.fax || '' })),
  );
  const hasHospital = Boolean(request.hospital);
  const [hospital, setHospital] = useState<Row>({
    name: request.hospital?.name || '',
    city_state: request.hospital?.city_state || '',
    fax: request.hospital?.fax || '',
  });
  const hasPharmacy = Boolean(request.pharmacy);
  const [pharmacy, setPharmacy] = useState<Row>({
    name: request.pharmacy?.name || '',
    city_state: request.pharmacy?.city_state || '',
    fax: request.pharmacy?.fax || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // Directory autofill: load saved offices, then prefill each row's fax +
  // location by a case-insensitive office-name match (only while the fax is
  // still blank). The per-row picker below also fills from this list.
  const [directory, setDirectory] = useState<ProviderOffice[]>([]);
  useEffect(() => {
    let alive = true;
    api.get('/api/crm/provider-offices').then((res) => {
      if (!alive) return;
      const list: ProviderOffice[] = res.data?.offices || [];
      setDirectory(list);
      const byName = new Map(list.map((o) => [o.office_name.trim().toLowerCase(), o]));
      const fill = (row: Row): Row => {
        if (row.fax.trim() || !row.name) return row;
        const m = byName.get(row.name.trim().toLowerCase());
        if (!m || !m.fax_number) return row;
        return { ...row, fax: m.fax_number, city_state: row.city_state || m.location || '' };
      };
      setOffices((rows) => rows.map(fill));
      setHospital((h) => fill(h));
      setPharmacy((p) => fill(p));
    }).catch(() => { /* directory is best-effort; manual entry still works */ });
    return () => { alive = false; };
  }, []);

  // Per-destination fax status (by last-10 digits) for a "sent/failed" hint.
  const statusByNum = new Map<string, string>();
  for (const f of request.faxes || []) statusByNum.set(last10(f.to_number), f.status);

  const setOffice = (i: number, k: keyof Row, v: string) =>
    setOffices((list) => list.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));

  const anyValid = offices.some((o) => faxValid(o.fax))
    || (hasHospital && faxValid(hospital.fax)) || (hasPharmacy && faxValid(pharmacy.fax));
  const anyInvalid = offices.some((o) => o.fax.trim() !== '' && !faxValid(o.fax))
    || (hasHospital && hospital.fax.trim() !== '' && !faxValid(hospital.fax))
    || (hasPharmacy && pharmacy.fax.trim() !== '' && !faxValid(pharmacy.fax));
  const canSend = anyValid && !anyInvalid && !saving;

  const send = async () => {
    if (!canSend || !request.signed_document_id) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post(`/api/crm/documents/${request.signed_document_id}/fax`, {
        offices: offices.map((o) => ({ name: o.name, city_state: o.city_state.trim(), fax: o.fax.trim() })),
        hospital: hasHospital ? { city_state: hospital.city_state.trim(), fax: hospital.fax.trim() } : null,
        pharmacy: hasPharmacy ? { city_state: pharmacy.city_state.trim(), fax: pharmacy.fax.trim() } : null,
      });
      const queued = res.data?.queued || [];
      if (queued.length === 0) {
        // Nothing new to send — every office already delivered/in-flight.
        setInfo(res.data?.note || 'Those offices already have a fax in progress or delivered.');
        setSaving(false);
        return;
      }
      onSent(queued.length);
    } catch (e: any) {
      const d = e?.response?.data || {};
      setError(d.detail || d.error || e.message || 'Could not send the fax(es).');
      setSaving(false);
    }
  };

  const statusPill = (fax: string) => {
    const st = statusByNum.get(last10(fax));
    if (!st) return null;
    const label = st === 'delivered' ? 'delivered' : st === 'failed' ? 'failed' : 'sending';
    const cls = st === 'delivered' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : st === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
        : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';
    return <span className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{label}</span>;
  };

  const officeRow = (label: string, row: Row, set: (k: keyof Row, v: string) => void, faxOptional = false) => (
    <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}{statusPill(row.fax)}</p>
      <div className="mt-1.5 space-y-2">
        {directory.length > 0 && (
          <div>
            <label className={lblCls}>Use a saved office <span className="font-normal">(optional)</span></label>
            <select
              className={inputCls}
              value=""
              onChange={(e) => {
                const o = directory.find((d) => d.id === e.target.value);
                if (o) {
                  if (o.fax_number) set('fax', o.fax_number);
                  if (o.location) set('city_state', o.location);
                }
                e.target.value = '';
              }}
            >
              <option value="">Pick from directory…</option>
              {directory.filter((d) => d.fax_number).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.office_name}{d.location ? ` — ${d.location}` : ''} ({fmtFax(d.fax_number)})
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className={lblCls}>Location <span className="font-normal">(optional)</span></label>
          <input className={inputCls} value={row.city_state} onChange={(e) => set('city_state', e.target.value)} placeholder="City, State" />
        </div>
        <div>
          <label className={lblCls}>Fax number {faxOptional && <span className="font-normal">(optional)</span>}</label>
          <input type="tel" inputMode="tel" className={inputCls} value={row.fax} onChange={(e) => set('fax', e.target.value)} placeholder="(919) 555-0142" />
          {row.fax.trim() !== '' && !faxValid(row.fax) && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Enter a 10-digit US fax number.</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-white p-5 shadow-xl dark:bg-gray-800 sm:rounded-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Fax to offices">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Fax the release</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Add each office's fax number (and location if you have it) — they print on the cover page. We fax the signed release to each.
        </p>

        <div className="mt-4 space-y-3">
          {offices.map((o, i) => (
            <div key={i}>{officeRow(o.name || `Office ${i + 1}`, o, (k, v) => setOffice(i, k, v))}</div>
          ))}
          {hasHospital && officeRow(hospital.name || 'Hospital', hospital, (k, v) => setHospital((h) => ({ ...h, [k]: v })), true)}
          {hasPharmacy && officeRow(pharmacy.name || 'Pharmacy', pharmacy, (k, v) => setPharmacy((p) => ({ ...p, [k]: v })), true)}
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {info && <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">{info}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0">Cancel</button>
          <button type="button" onClick={send} disabled={!canSend} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Sending…' : 'Send fax(es)'}
          </button>
        </div>
      </div>
    </div>
  );
}
